import type { CatalogEntry } from "./types";
import { catalogEntries } from "./fixtures";

const CACHE_KEY = "taskjinn.catalog.index";
const ETAG_KEY = "taskjinn.catalog.etag";

interface IndexEntry {
  entry_id: string;
  display_name: string;
  /** Space-joined. */
  aliases?: string;
  kind: CatalogEntry["kind"];
  category: string;
  tier: CatalogEntry["tier"];
  availability: CatalogEntry["availability"];
  supported: boolean;
  /** Comma-joined. */
  auth?: string;
  /** Space-joined. */
  match_domains?: string;
  popularity?: number;
}

const join = (value: string | undefined, separator: " " | ","): string[] =>
  value ? value.split(separator).filter(Boolean) : [];

/** Detail fields are lazy; the index is only enough to search and show a tile. */
function toEntry(entry: IndexEntry): CatalogEntry {
  return {
    entry_id: entry.entry_id,
    display_name: entry.display_name,
    aliases: join(entry.aliases, " "),
    kind: entry.kind,
    category: entry.category,
    tier: entry.tier,
    availability: entry.availability,
    supported: entry.supported,
    auth: join(entry.auth, ","),
    match_domains: join(entry.match_domains, " "),
    popularity: entry.popularity ?? 0,
    description: "",
    logo_ref: undefined,
    allowed_domains: [],
    capabilities: [],
    search_keywords: [],
  };
}

function cached(): CatalogEntry[] | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { entries: IndexEntry[] };
    return parsed.entries.map(toEntry);
  } catch {
    return null;
  }
}

/**
 * One conditional request per session against the Worker's KV-backed index. Falls back to the
 * bundled seed (then to the session cache) so the builder never blocks on the catalog.
 */
export async function fetchCatalogIndex(fetchImpl: typeof fetch = fetch): Promise<CatalogEntry[]> {
  const headers: Record<string, string> = {};
  if (typeof sessionStorage !== "undefined") {
    const etag = sessionStorage.getItem(ETAG_KEY);
    if (etag) headers["if-none-match"] = etag;
  }
  try {
    const response = await fetchImpl("/api/v1/catalog/index", { headers });
    if (response.status === 304) {
      return cached() ?? catalogEntries;
    }
    if (!response.ok) throw new Error(`catalog index ${response.status}`);
    const body = (await response.json()) as { entries: IndexEntry[] };
    const entries = body.entries.map(toEntry);
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(body));
      const etag = response.headers.get("etag");
      if (etag) sessionStorage.setItem(ETAG_KEY, etag);
    }
    return entries;
  } catch {
    return cached() ?? catalogEntries;
  }
}
